import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import type { HrRecruitmentPipeline } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const STAGE_ORDER = ['APPLIED', 'SCREENING', 'INTERVIEW', 'OFFER', 'JOINED', 'REJECTED'];
const NEXT_STAGE: Record<string, string> = {
  APPLIED: 'SCREENING',
  SCREENING: 'INTERVIEW',
  INTERVIEW: 'OFFER',
  OFFER: 'JOINED',
};

export function HrRecruitmentPage() {
  const [pipeline, setPipeline] = useState<HrRecruitmentPipeline | null>(null);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPipeline(await hrApi.recruitmentPipeline());
    } catch (err) {
      showApiError(err, 'Failed to load recruitment pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const advance = async (candidateId: number, currentStage: string) => {
    const next = NEXT_STAGE[currentStage];
    if (!next) return;
    setActingId(candidateId);
    try {
      await hrApi.moveCandidateStage(candidateId, next);
      notify.success(`Moved to ${next}`);
      load();
    } catch (err) {
      showApiError(err, 'Failed to move candidate');
    } finally {
      setActingId(null);
    }
  };

  const hire = async (candidateId: number) => {
    setActingId(candidateId);
    try {
      await hrApi.hireCandidate(candidateId);
      notify.success('Candidate hired');
      load();
    } catch (err) {
      showApiError(err, 'Hire failed — ensure offer is approved');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Recruitment"
        description="ATS pipeline — applied through hired or rejected"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Recruitment' }]}
      />
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      <div className="flex gap-3 overflow-x-auto pb-4">
        {STAGE_ORDER.map((stage) => (
          <div
            key={stage}
            className="min-w-[220px] flex-shrink-0 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
          >
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{stage}</h3>
            <ul className="space-y-2">
              {(pipeline?.stages?.[stage] ?? []).map((c) => (
                <li key={c.id} className="rounded bg-white p-2 text-sm shadow-sm dark:bg-slate-800">
                  <div className="font-medium">{c.fullName}</div>
                  <div className="text-xs text-slate-500">{c.email ?? '—'}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {NEXT_STAGE[stage] && (
                      <Button
                        variant="secondary"
                        loading={actingId === c.id}
                        onClick={() => void advance(c.id, stage)}
                      >
                        → {NEXT_STAGE[stage]}
                      </Button>
                    )}
                    {stage === 'OFFER' && (
                      <Button loading={actingId === c.id} onClick={() => void hire(c.id)}>
                        Hire
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
